import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Users, Info, Loader, X, Image } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';
import { useAuth } from '../../contexts/AuthContext';
import { sportTypeAPI, teammatesAPI } from '../../services/api';
import { teamAPI } from '../services';
import { useSquads } from '../../squads/hooks/useSquads';
import TeamFormation from '../components/TeamFormation';
import Breadcrumb from '../../components/common/Breadcrumb';
import teamImageService from '../services/teamImageService';
import ImageCropModal, { ALLOWED_IMAGE_TYPES } from '../../components/common/ImageCropModal';
import { useImagePicker } from '../../hooks/useImagePicker.jsx';
import { apiCall } from '../../services/apiUtil';
import { moveImage } from '../../services/imageUploadService';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

export default function CreateTeamPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, groupId: groupIdParam, squadId } = useParams();
  const groupId = groupIdParam || squadId;
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { user, isAuthenticated } = useAuth();
  const { getGroup } = useSquads();
  const isEditMode = Boolean(id);

  // Get return URL from query params (passed by calling context)
  const returnUrl = searchParams.get('returnUrl');

  const effectiveGroupId = groupId;

  // Get owner info from URL params (for /squads/:groupId/teams/*) or query params
  const ownerTypeFromUrl = effectiveGroupId ? 'group' : (searchParams.get('ownerType') || 'user');
  const ownerIdFromUrl = effectiveGroupId || searchParams.get('ownerId');
  const memberGroupId = searchParams.get('memberGroupId'); // group to fetch members from (used when ownerType=tournament)
  const eventIdFromUrl = searchParams.get('eventId') ? parseInt(searchParams.get('eventId')) : null;
  const ownerLabel = searchParams.get('ownerLabel');       // display name of the owner
  const sportTypeIdFromUrl = searchParams.get('sportTypeId') ? parseInt(searchParams.get('sportTypeId')) : null;
  const fromGameContext = searchParams.get('fromGame') === 'true';
  const teamTypeFromUrl = searchParams.get('teamType') || null;

  // Always save to database by default, unless explicitly coming from a game context
  // Profile -> Teams: always save (ownerType=user, no toggle)
  // Groups -> Teams: always save (ownerType=group, no toggle)
  // Game/Event setup: show toggle (fromGame=true in URL)
  const shouldShowSaveToggle = fromGameContext;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sportTypeId: null,
    teamType: teamTypeFromUrl || 'team',
    maxPlayersPerTeam: 20,
    logo: '🏆'
  });

  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState('');

  // Sports list state
  const [sports, setSports] = useState([]);
  const [sportsLoading, setSportsLoading] = useState(false);

  // Available members state
  const [availableMembers, setAvailableMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Team count state (always 1 in edit mode)
  const [teamCount, setTeamCount] = useState(1);

  // Existing team data for edit mode
  const [existingTeam, setExistingTeam] = useState(null);
  const [initialTeams, setInitialTeams] = useState(null);

  // Group data for group context
  const [groupData, setGroupData] = useState(null);

  // Image upload state
  const [uploadedImage, setUploadedImage] = useState(null);
  const [pendingImageUrl, setPendingImageUrl] = useState(null);

  // Crop modal state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedImageForCrop, setSelectedImageForCrop] = useState(null);

  const ALL_TEAM_TYPES = [
    { value: 'single', label: 'Single Player', description: 'Individual competition' },
    { value: 'double', label: 'Doubles/Pair', description: 'Two-player team' },
    { value: 'team', label: 'Team', description: 'Multiple players (2-20)' }
  ];

  // maxPlayersPerSide is how many players are actively on court per side in a
  // single game (e.g. 2 for doubles pickleball) — it says nothing about how
  // large a team's roster can be, since a subset of the roster can play any
  // given game (MLP-style). Team type is always available regardless of sport.
  const teamTypes = ALL_TEAM_TYPES;

  const logos = ['🏆', '⚡', '🏀', '🏐', '🎾', '⚽', '🏸', '🏓', '🔥', '💪', '⭐', '🦅', '🐉', '🦁', '🎯'];

  // Fetch sport types on component mount
  useEffect(() => {
    const fetchSportTypes = async () => {
      setSportsLoading(true);
      try {
        const response = await sportTypeAPI.getAll();
        const sportTypesData = response?.data || response || [];
        setSports(sportTypesData);

        // Sport type priority: URL param > group > first in list
        if (!isEditMode && sportTypeIdFromUrl) {
          setFormData(prev => ({ ...prev, sportTypeId: sportTypeIdFromUrl }));
        } else if (sportTypesData.length > 0 && !formData.sportTypeId) {
          setFormData(prev => ({
            ...prev,
            sportTypeId: sportTypesData[0].id
          }));
        }
      } catch (error) {
        console.error('Failed to fetch sport types:', error);
      } finally {
        setSportsLoading(false);
      }
    };

    fetchSportTypes();
  }, []);

  // Fetch group details when coming from group context
  useEffect(() => {
    const fetchGroupDetails = async () => {
      if (ownerTypeFromUrl !== 'group' || !ownerIdFromUrl) return;

      try {
        const result = await getGroup(ownerIdFromUrl);
        if (result.success && result.data) {
          setGroupData(result.data);

          // Set sport type from group if available and not in edit mode
          // (URL sportTypeId takes precedence if both are present)
          const groupSportTypeId = result.data.sportTypeId || result.data.sport_type_id;
          if (groupSportTypeId && !isEditMode && !sportTypeIdFromUrl) {
            setFormData(prev => ({
              ...prev,
              sportTypeId: groupSportTypeId
            }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch group details:', error);
      }
    };

    fetchGroupDetails();
  }, [ownerTypeFromUrl, ownerIdFromUrl, getGroup, isEditMode]);

  // Fetch available members based on owner type
  useEffect(() => {
    const fetchMembers = async () => {
      if (!user?.id) return;

      setMembersLoading(true);
      try {
        // Create current user object to add to the list
        const currentUser = {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          name: `${user.firstName} ${user.lastName}`.trim() || 'Me',
          profileImage: user.profileImage,
          isCurrentUser: true,
        };

        let members = [];

        if (eventIdFromUrl) {
          // Fetch only members who responded 'available' to the tournament event
          const result = await apiCall(`/events/${eventIdFromUrl}/responses`);
          const responses = result?.data || result || [];
          members = responses
            .filter(r => r.status === 'available' && r.user?.id != null)
            .map(r => ({
              id: r.user.id,
              firstName: r.user.first_name,
              lastName: r.user.last_name,
              name: `${r.user.first_name} ${r.user.last_name}`.trim(),
              profileImage: r.user.profile_image || null,
            }));
        } else {
          const groupIdForMembers = ownerTypeFromUrl === 'group' ? ownerIdFromUrl : memberGroupId;
          if (groupIdForMembers) {
            // Fetch group members directly from API
            const result = await apiCall(`/groups/${groupIdForMembers}/members?page=1&limit=50`);
            if (result.success && result.data) {
              const actualMembers = result.data.filter(m => m.userId != null && !m.isInvitation);
              members = actualMembers.map(member => ({
                id: member.userId,
                firstName: member.firstName,
                lastName: member.lastName,
                name: member.username || `${member.firstName} ${member.lastName}`.trim(),
                profileImage: member.profilePicture,
              }));
            } else {
              toast.error('Failed to load group members');
            }
          } else {
            // Fetch teammates from Fastify API
            const result = await teammatesAPI.getAll(1, 100);
            const teammatesData = result?.data || result || [];
            members = teammatesData
              .filter(teammate => teammate.user?.id != null)
              .map(teammate => ({
                id: teammate.user.id,
                firstName: teammate.user.firstName,
                lastName: teammate.user.lastName,
                name: `${teammate.user.firstName} ${teammate.user.lastName}`.trim(),
                profileImage: teammate.user.profileImage,
              }));
          }
        }

        // Add current user at the beginning only if not already in the list.
        // When coming from an event, the responses filter already excludes anyone
        // who didn't respond 'available' — so don't force-add the current user here.
        const currentUserId = String(user.id);
        const isCurrentUserInList = members.some(m => String(m.id) === currentUserId);
        if (!isCurrentUserInList && !eventIdFromUrl) {
          members = [currentUser, ...members];
        } else {
          // Mark the current user in the list
          members = members.map(m => String(m.id) === currentUserId ? { ...m, isCurrentUser: true } : m);
        }

        // Ensure unique members by ID (remove duplicates)
        const seenIds = new Set();
        members = members.filter(m => {
          const idStr = String(m.id);
          if (seenIds.has(idStr)) return false;
          seenIds.add(idStr);
          return true;
        });

        setAvailableMembers(members);
      } catch (error) {
        console.error('Failed to fetch members:', error);
        setAvailableMembers([{
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          name: `${user.firstName} ${user.lastName}`.trim() || 'Me',
          profileImage: user.profileImage,
          isCurrentUser: true,
        }]);
      } finally {
        setMembersLoading(false);
      }
    };

    fetchMembers();
  }, [user?.id, ownerTypeFromUrl, ownerIdFromUrl, memberGroupId, eventIdFromUrl]);

  // Load team data when in edit mode
  useEffect(() => {
    const loadTeamData = async () => {
      if (!isEditMode || !id) return;

      setLoading(true);
      try {
        const response = await teamAPI.getById(id);
        const teamData = response?.data || response;

        if (teamData) {
          setExistingTeam(teamData);

          const existingLogo = teamData.teamLogo || teamData.team_logo || '🏆';
          // New format: /images/... path; legacy: bare filename with extension; otherwise emoji
          const isNewFormat = existingLogo?.startsWith('/images/');
          const isLegacyFile = !isNewFormat && existingLogo && existingLogo.includes('.');
          const isImageLogo = isNewFormat || isLegacyFile;

          setFormData({
            name: teamData.teamName || teamData.team_name || teamData.name || '',
            description: teamData.description || '',
            sportTypeId: teamData.sportTypeId || teamData.sport_type_id || null,
            teamType: teamData.teamType || (teamData.maxMembers === 1 || teamData.max_members === 1 ? 'single' : teamData.maxMembers === 2 || teamData.max_members === 2 ? 'double' : 'team'),
            maxPlayersPerTeam: teamData.maxMembers || teamData.max_members || 20,
            logo: isImageLogo ? null : existingLogo
          });

          // Load existing image for preview
          if (isNewFormat) {
            // New format: use directly as preview
            setUploadedImage({ url: resolveImageUrl(existingLogo), path: existingLogo });
          } else if (isLegacyFile) {
            // Legacy format: fetch presigned URL for preview
            const loadTeamLogoUrl = async () => {
              try {
                const url = await teamImageService.getTeamLogoUrl(id, existingLogo);
                if (url) {
                  setUploadedImage({ url, path: existingLogo });
                }
              } catch (error) {
                console.error('Failed to load team logo:', error);
              }
            };
            loadTeamLogoUrl();
          }

          // Format existing members for initialTeams — skip any without a userId
          const members = teamData.members || [];
          const formattedPlayers = members
            .filter(member => member.userId != null)
            .map(member => ({
              id: member.userId,
              name: `${member.user.firstName} ${member.user.lastName}`.trim(),
              firstName: member.user.firstName,
              lastName: member.user.lastName,
              profileImage: member.user.profileImage,
              isCurrentUser: member.userId === user?.id,
            }));

          // Set initial teams for TeamFormation
          setInitialTeams([{
            name: teamData.teamName || 'Team A',
            players: formattedPlayers,
          }]);

          // Add existing members to available members (they'll be filtered out by TeamFormation)
          setAvailableMembers(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newMembers = formattedPlayers.filter(p => !existingIds.has(p.id));
            return [...prev, ...newMembers];
          });
        }
      } catch (error) {
        console.error('Failed to load team data:', error);
        setError('Failed to load team: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    loadTeamData();
  }, [isEditMode, id, user?.id]);

  const getMaxPlayers = useCallback(() => {
    switch (formData.teamType) {
      case 'single': return 1;
      case 'double': return 2;
      case 'team': return formData.maxPlayersPerTeam || 20;
      default: return formData.maxPlayersPerTeam || 20;
    }
  }, [formData.teamType, formData.maxPlayersPerTeam]);

  const getMinPlayers = useCallback(() => {
    switch (formData.teamType) {
      case 'single': return 1;
      case 'double': return 2;
      case 'team': return 2;
      default: return 2;
    }
  }, [formData.teamType]);

  // Get sport name for display (from group or sports list)
  const sportName = useMemo(() => {
    // First try to get from group data
    if (groupData) {
      const name = groupData.sportName || groupData.sport_name || groupData.sport?.name || groupData.sport?.sportName;
      if (name) return name;
    }
    // Fall back to finding in sports list
    if (formData.sportTypeId && sports.length > 0) {
      const sport = sports.find(s => s.id === formData.sportTypeId);
      return sport?.sportName || sport?.name || null;
    }
    return null;
  }, [groupData, formData.sportTypeId, sports]);

  // Reset teamType to first valid option if the selected sport restricts it
  useEffect(() => {
    if (formData.teamType && !teamTypes.find(t => t.value === formData.teamType)) {
      setFormData(prev => ({ ...prev, teamType: teamTypes[0]?.value || 'single' }));
    }
  }, [teamTypes]);

  // Determine if we should show settings info in TeamFormation (when from group context)
  const showSettingsInFormation = ownerTypeFromUrl === 'group' && sportName;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'sportTypeId' ? parseInt(value) : value
    }));
  };

  const { openPicker, pickerInput } = useImagePicker({
    accept: 'image/jpeg,image/png,image/gif,image/webp',
    onFiles: (files) => {
      const file = files[0]
      if (!file) return
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error('Please upload a JPEG, PNG, GIF, or WebP image')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image size must be less than 10MB')
        return
      }
      const reader = new FileReader()
      reader.onloadend = () => {
        setSelectedImageForCrop(reader.result)
        setCropModalOpen(true)
      }
      reader.readAsDataURL(file)
    },
  });

  // Called only on moderation success, with the temp upload result { url, key }
  const handleCroppedImage = async ({ url }) => {
    setUploadedImage({ url: resolveImageUrl(url), path: url });
    setFormData(prev => ({ ...prev, logo: null }));
    if (isEditMode && id) {
      // Move immediately to canonical path since we have the entity ID
      const { url: canonicalUrl } = await moveImage(url, `team_assets/${id}/${crypto.randomUUID()}.jpeg`);
      setUploadedImage({ url: resolveImageUrl(canonicalUrl), path: canonicalUrl });
    } else {
      setPendingImageUrl(url);
    }
  };

  const removeUploadedImage = () => {
    setPendingImageUrl(null);
    setUploadedImage(null);
    setFormData(prev => ({ ...prev, logo: '🏆' }));
  };

  const handleTeamsFormed = async (teamsData) => {
    if (teamsData.saved) {
      // Move temp upload to canonical path and save for each created team
      if (pendingImageUrl && !isEditMode && teamsData.teams?.length > 0) {
        for (const savedTeam of teamsData.teams) {
          const teamId = savedTeam.savedId || savedTeam.savedData?.id;
          if (teamId) {
            try {
              const { url: canonicalUrl } = await moveImage(
                pendingImageUrl,
                `team_assets/${teamId}/${crypto.randomUUID()}.jpeg`
              );
              await apiCall(`/teams/${teamId}`, {
                method: 'PATCH',
                body: JSON.stringify({ teamLogo: canonicalUrl }),
              });
            } catch (error) {
              console.error('Failed to save team logo for team', teamId, error);
              toast.error('Team created but logo save failed. You can update it by editing the team.');
            }
          }
        }
        setPendingImageUrl(null);
      }

      // Teams were saved to backend
      const message = isEditMode
        ? 'Team updated successfully!'
        : `${teamsData.teams.length} team${teamsData.teams.length > 1 ? 's' : ''} created successfully!`;
      toast.success(message);

      // Navigate back based on returnUrl or context
      if (returnUrl) {
        // Use explicit return URL if provided (decode first — it was encoded by caller)
        navigate(decodeURIComponent(returnUrl));
      } else if (ownerTypeFromUrl === 'group' && ownerIdFromUrl) {
        navigate(`/squads/${ownerIdFromUrl}?tab=teams`);
      } else if (location.pathname.startsWith('/profile/teams')) {
        navigate('/profile/teams');
      } else {
        navigate('/teams');
      }
    } else {
      // Teams returned without saving (for game use)
      // This happens when fromGame=true and user unchecked "Save team for future use"
      toast.success('Teams formed successfully!');
      // Navigate back - the calling context should handle the teams data
      navigate(-1);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  // Build breadcrumb items based on current path (must be before early returns)
  const breadcrumbItems = useMemo(() => {
    const path = location.pathname;
    const teamName = existingTeam?.teamName || existingTeam?.team_name || existingTeam?.name || 'Team';

    if (path.startsWith('/profile/teams')) {
      const items = [
        { label: 'Profile', path: '/profile' },
        { label: 'Teams', path: '/profile/teams' },
      ];
      if (isEditMode && id) {
        items.push({ label: teamName, path: `/profile/teams/${id}` });
        items.push({ label: 'Edit' });
      } else {
        items.push({ label: 'Create Team' });
      }
      return items;
    } else if (ownerTypeFromUrl === 'group' && ownerIdFromUrl) {
      const squadName = groupData?.name || groupData?.groupName || 'Squad';
      const items = [
        { label: 'Squads', path: '/squads' },
        { label: squadName, path: `/squads/${ownerIdFromUrl}` },
        { label: 'Teams', path: `/squads/${ownerIdFromUrl}?tab=teams` },
      ];
      if (isEditMode && id) {
        items.push({ label: teamName, path: `/squads/${ownerIdFromUrl}/teams/${id}` });
        items.push({ label: 'Edit' });
      } else {
        items.push({ label: 'Create Team' });
      }
      return items;
    }

    // Default breadcrumbs
    if (isEditMode && id) {
      return [
        { label: 'Teams', path: '/teams' },
        { label: teamName, path: `/teams/${id}` },
        { label: 'Edit' }
      ];
    }
    return [
      { label: 'Teams', path: '/teams' },
      { label: 'Create Team' }
    ];
  }, [location.pathname, isEditMode, ownerTypeFromUrl, ownerIdFromUrl, id, existingTeam, groupData]);

  if (!isAuthenticated) {
    return (
      <div className={`min-h-screen ${theme.background} py-6 px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-2xl mx-auto text-center">
          <Users className={`w-16 h-16 ${theme.textMuted} mx-auto mb-4`} />
          <h1 className={`text-2xl font-bold ${theme.text} mb-4`}>Sign In Required</h1>
          <p className={`${theme.textSecondary} mb-6`}>You need to be signed in to create a team</p>
          <button
            onClick={() => navigate('/signin')}
            className={`${btn.base} ${btn.md} ${theme.buttonPrimary}`}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.background} py-6 px-4 flex items-center justify-center`}>
        <Loader className={`w-8 h-8 ${theme.textSecondary} animate-spin`} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.background} py-6 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-4">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={handleBack}
            className={`mr-4 p-2 ${theme.cardHover} rounded-lg transition-colors`}
          >
            <ArrowLeft className={`w-6 h-6 ${theme.textSecondary}`} />
          </button>
          <div>
            <h1 className={`text-3xl font-bold ${theme.text}`}>{isEditMode ? 'Edit Team' : 'Create Team'}</h1>
            <p className={`${theme.textSecondary} mt-1`}>
              {isEditMode ? 'Update team information and manage members' : 'Build your sports team and add players'}
            </p>
          </div>
        </div>

        {/* Ownership info banner */}
        {!isEditMode && (
          <div className={`mb-6 px-4 py-3 rounded-lg flex items-center gap-2 text-sm font-medium ${
            ownerTypeFromUrl === 'tournament'
              ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800'
              : ownerTypeFromUrl === 'group'
              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800'
              : 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-800'
          }`}>
            <span>
              {ownerTypeFromUrl === 'tournament'
                ? `Temporary teams — owned by tournament: ${ownerLabel || 'Tournament'}`
                : ownerTypeFromUrl === 'group'
                ? `Permanent teams — owned by squad: ${ownerLabel || groupData?.name || 'Squad'}`
                : `Permanent teams — owned by you`}
            </span>
          </div>
        )}

        <div className="space-y-8">
          {/* Basic Team Information */}
          <div className={`${theme.card} rounded-xl ${theme.shadow} p-6`}>
            <h2 className={`text-xl font-semibold ${theme.text} mb-6 flex items-center`}>
              <Info className="w-5 h-5 mr-2 text-blue-500" />
              Team Settings
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Sport *
                  {ownerTypeFromUrl === 'group' && groupData && (
                    <span className={`ml-2 text-xs ${theme.textMuted} font-normal`}>(from squad)</span>
                  )}
                  {sportTypeIdFromUrl && ownerTypeFromUrl !== 'group' && (
                    <span className={`ml-2 text-xs ${theme.textMuted} font-normal`}>(from tournament)</span>
                  )}
                </label>
                <select
                  name="sportTypeId"
                  value={formData.sportTypeId || ''}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent ${(ownerTypeFromUrl === 'group' && groupData) || sportTypeIdFromUrl ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  required
                  disabled={sportsLoading || (ownerTypeFromUrl === 'group' && groupData) || Boolean(sportTypeIdFromUrl)}
                >
                  {sportsLoading ? (
                    <option value="">Loading sports...</option>
                  ) : (
                    sports.map((sport) => (
                      <option key={sport.id} value={sport.id}>
                        {sport.sportName || sport.name}
                      </option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Team Type *
                </label>
                <select
                  name="teamType"
                  value={formData.teamType}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  required
                >
                  {teamTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label} - {type.description}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  placeholder="Add a description for your team (optional)"
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none`}
                />
              </div>

              {!isEditMode && (
                <div>
                  <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                    Number of Teams to Create
                  </label>
                  <select
                    value={teamCount}
                    onChange={(e) => setTeamCount(parseInt(e.target.value))}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  >
                    {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
                      <option key={n} value={n}>{n} {n === 1 ? 'Team' : 'Teams'}</option>
                    ))}
                  </select>
                </div>
              )}

              {formData.teamType === 'team' && (
                <div>
                  <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                    Max Players per Team
                  </label>
                  <input
                    type="number"
                    name="maxPlayersPerTeam"
                    value={formData.maxPlayersPerTeam}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({ ...prev, maxPlayersPerTeam: val === '' ? '' : parseInt(val) }));
                    }}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value);
                      if (e.target.value === '' || isNaN(val) || val < 1 || val > 100) {
                        setFormData(prev => ({ ...prev, maxPlayersPerTeam: 20 }));
                      }
                    }}
                    min={1}
                    max={100}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                </div>
              )}

              <div className="md:col-span-2">
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Team Logo
                </label>

                {/* Uploaded Image Preview */}
                {uploadedImage && (
                  <div className="mb-4 flex items-center gap-4">
                    <div className="relative">
                      <img
                        src={uploadedImage.url}
                        alt="Team logo"
                        className="w-20 h-20 rounded-lg object-cover border-2 border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={removeUploadedImage}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <span className={`text-sm ${theme.textSecondary}`}>
                      {uploadedImage.path ? 'Custom image uploaded' : 'Image selected (will upload after save)'}
                    </span>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  {/* Image Upload Button */}
                  {pickerInput}
                  <button
                    type="button"
                    onClick={openPicker}
                    className={`w-10 h-10 rounded-lg border-2 border-dashed transition-colors flex items-center justify-center ${
                      uploadedImage
                        ? 'border-blue-500 bg-blue-50'
                        : `border-gray-300 ${theme.cardHover}`
                    }`}
                    title="Upload custom image"
                  >
                    <Image className="w-5 h-5 text-gray-500" />
                  </button>

                  <span className={`text-sm ${theme.textMuted} mx-2`}>or</span>

                  {/* Emoji Selection */}
                  {logos.map(logo => (
                    <button
                      key={logo}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, logo }));
                        setUploadedImage(null); // Clear uploaded image when emoji selected
                      }}
                      className={`w-10 h-10 text-xl rounded-lg border-2 transition-colors ${
                        formData.logo === logo && !uploadedImage
                          ? 'border-blue-500 bg-blue-50'
                          : `border-gray-300 ${theme.cardHover}`
                      }`}
                    >
                      {logo}
                    </button>
                  ))}
                </div>
                <p className={`text-xs ${theme.textMuted} mt-2`}>
                  Upload a custom image (max 10MB) or choose an emoji
                </p>
              </div>
            </div>
          </div>

          {/* Team Formation */}
          <div className={`${theme.card} rounded-xl ${theme.shadow} p-6`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-xl font-semibold ${theme.text} flex items-center`}>
                <Users className="w-5 h-5 mr-2 text-green-500" />
                Form Your Team{teamCount > 1 ? 's' : ''}
              </h2>
              {membersLoading && (
                <Loader className={`w-5 h-5 ${theme.textSecondary} animate-spin`} />
              )}
            </div>

            {membersLoading ? (
              <div className="text-center py-12">
                <Loader className={`w-8 h-8 ${theme.textSecondary} animate-spin mx-auto mb-4`} />
                <p className={theme.textSecondary}>Loading available members...</p>
              </div>
            ) : availableMembers.length === 0 ? (
              <div className="text-center py-12">
                <Users className={`w-16 h-16 ${theme.textMuted} mx-auto mb-4`} />
                <h4 className={`text-lg font-medium ${theme.text} mb-2`}>No members available</h4>
                <p className={theme.textSecondary}>
                  {ownerTypeFromUrl === 'group'
                    ? 'This group has no members yet. Add members to the group first.'
                    : 'You have no teammates yet. Connect with players first.'}
                </p>
              </div>
            ) : (
              <TeamFormation
                availableMembers={availableMembers}
                ownerId={ownerIdFromUrl || String(user?.id)}
                ownerType={ownerTypeFromUrl}
                gameType={formData.teamType}
                maxPlayersPerTeam={getMaxPlayers()}
                minPlayersPerTeam={getMinPlayers()}
                teamCount={isEditMode ? 1 : teamCount}
                initialTeams={initialTeams || (formData.name ? [{ name: formData.name, players: [] }] : null)}
                showConfirmButton={true}
                confirmButtonText={isEditMode ? 'Update Team' : `Create ${teamCount > 1 ? 'Teams' : 'Team'}`}
                saveToBackend={!shouldShowSaveToggle}
                showSaveToggle={shouldShowSaveToggle}
                sportTypeId={formData.sportTypeId}
                onTeamsFormed={handleTeamsFormed}
                onCancel={handleBack}
                editTeamId={isEditMode ? parseInt(id) : null}
                existingMembers={initialTeams?.[0]?.players || []}
                teamDescription={formData.description}
                teamLogo={uploadedImage?.path || formData.logo}
                teamLogoUrl={uploadedImage?.url}
                sportName={sportName}
                showSettingsInfo={showSettingsInFormation}
                createdByUserId={user?.id}
                availablePlayersCount={eventIdFromUrl ? availableMembers.length : null}
              />
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>
      </div>

      <ImageCropModal
        isOpen={cropModalOpen}
        onClose={() => { setCropModalOpen(false); setSelectedImageForCrop(null); }}
        imageSrc={selectedImageForCrop}
        fileType="team_assets"
        onCropComplete={handleCroppedImage}
        shape="rect"
        aspectRatio={1}
      />
    </div>
  );
}
